// ============================================================
// HARMONIC CHARTER — Cloud Folders (Supabase)
// ============================================================
//
// Supabase table required (run in SQL editor):
//
//   create table folders (
//     id         text primary key,
//     owner_id   uuid references auth.users(id) on delete cascade not null,
//     name       text not null,
//     position   integer not null default 0,
//     created_at timestamptz default now()
//   );
//   alter table folders enable row level security;
//   create policy "Users manage own folders"
//     on folders for all
//     using (auth.uid() = owner_id)
//     with check (auth.uid() = owner_id);
//
// ============================================================

import { supabase } from '../lib/supabase';
import type { Folder } from './library';

// ── Pull all folders for the signed-in user ───────────────────

export async function pullFolders(): Promise<Folder[]> {
  const { data, error } = await supabase
    .from('folders')
    .select('id, name, position')
    .order('position', { ascending: true });

  if (error) {
    console.error('[cloudFolders] pull error:', error.message);
    return [];
  }

  return (data ?? []).map((row) => ({
    id:    row.id,
    name:  row.name,
    order: row.position,
  }));
}

// ── Upsert a single folder ────────────────────────────────────

export async function pushFolder(folder: Folder, position: number): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { error } = await supabase
    .from('folders')
    .upsert({
      id:       folder.id,
      owner_id: user.id,
      name:     folder.name,
      position,
    });

  if (error) console.error('[cloudFolders] push error:', error.message);
}

// ── Delete a folder from Supabase ─────────────────────────────

export async function deleteCloudFolder(folderId: string): Promise<void> {
  const { error } = await supabase
    .from('folders')
    .delete()
    .eq('id', folderId);

  if (error) console.error('[cloudFolders] delete error:', error.message);
}

// ── Push the full ordered folder list (used on reorder) ───────

export async function pushAllFolders(folders: Folder[]): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const rows = folders.map((f, i) => ({
    id:       f.id,
    owner_id: user.id,
    name:     f.name,
    position: i,
  }));

  const { error } = await supabase
    .from('folders')
    .upsert(rows);

  if (error) console.error('[cloudFolders] pushAll error:', error.message);
}